import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { useToast } from '../components/ui/use-toast';
import { useProfileDialog } from './ProfileDialogProvider';
import { INSUFFICIENT_CREDITS } from '@/services/api';
import { ELEMENT_POOL_EXHAUSTED } from '@superlekcja/shared';
import { useTranslation } from 'react-i18next';
import { useElementPool } from './ElementPoolContext';
import { track } from '@/utils/analytics';

interface ErrorHandlerContextType {
  handleError: (error: unknown, description: string) => void;
}

const ErrorHandlerContext = createContext<ErrorHandlerContextType | undefined>(undefined);

export function ErrorHandlerProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const { openDialog: showProfileDialog } = useProfileDialog();
  const { openTopUp } = useElementPool();
  const { t } = useTranslation();

  const handleError = useCallback((error: unknown, description: string) => {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    // Wyczerpana pula elementow lekcji to nie blad do pokazania, tylko pytanie: dokupic kolejna?
    if (errorMessage === ELEMENT_POOL_EXHAUSTED) {
      openTopUp();
      return;
    }
    if (errorMessage === INSUFFICIENT_CREDITS) {
      track('credits_exhausted');
      // Oferta doladowania jest w oknie profilu — modal zaslania toast, wiec przycisku w toascie nie ma.
      showProfileDialog();
      toast({
        variant: "destructive",
        title: t('credits-survey.toast-title'),
        description: t('credits-survey.toast-description'),
      });
    } else {
      toast({
        variant: "destructive",
        title: "Błąd",
        description,
      });
    }
  }, [showProfileDialog, openTopUp, toast, t]);

  return (
    <ErrorHandlerContext.Provider value={{ handleError }}>
      {children}
    </ErrorHandlerContext.Provider>
  );
}

export function useErrorHandler() {
  const context = useContext(ErrorHandlerContext);
  if (context === undefined) {
    throw new Error('useErrorHandler must be used within an ErrorHandlerProvider');
  }
  return context;
} 